// Player is the `app`. It requires and includes all the components then renders
// them into the templates.

import ./lib/extensions

Base from ./base.civet

Persistence from ./persistence.civet
PlayerAudio from ./player-audio.civet
PracticeMode from ./practice.civet
Renderer from ./renderer.civet
Spritesheet from ./lib/spritesheet.civet
ToolsMixin from ./tools.civet

{
  Modal
  Observable
  Progress
} from @danielx/dui

AboutTemplate from ./templates/about
AppTemplate from ./templates/app
BuyNowTemplate from ./templates/buy-now
ExportTemplate from ./templates/export
HelpTemplate from ./templates/help
OptionTemplate from ./templates/option
PersistenceTemplate from ./templates/persistence

MidiDeviceInput from ./lib/midi/input.civet
MidiSong from ./lib/midi/song.civet

Shaders from ./lib/shaders.civet

Actions from ./views/actions.civet
AccountSongPicker from ./views/account-song-picker.civet
AdSlot from ./views/ad-slot.civet
DemoPicker from ./views/demo-picker.civet
FXPicker from ./views/fx-picker.civet
GameResults from ./views/game-results.civet
MeterPicker from ./views/meter-picker.civet
MidiInput from ./views/midi-input.civet
ModeHud from ./views/mode-hud.civet
ModeModal from ./views/mode-modal.civet
MyPractice from ./views/my-practice.civet
NoteControl from ./views/note-control.civet
Patterns from ./views/patterns.civet
PracticeSetup from ./views/practice-setup.civet
SongCollection from ./views/song-collection.civet
RenderModal from ./views/render-modal.civet
Settings from ./views/settings.civet
SignaturePicker from ./views/signature-picker.civet
ThemeEditor from ./views/theme-editor.civet
Tools from ./views/tools.civet
VFXEditor from ./views/vfx-editor.civet

{
  CUSTOM_PRESET_NAME
  DEFAULT_SYNTH_SHA
  LEGACY_SYNTH_SHA
  THEME_KEY
} from ./const.civet

{ clamp } from @danielx/lib

{
  rand
  staffNoteToMidiPitch
} from ./lib/util.civet

{
  purchase
  loadStripe
} from ./lib/stripe-checkout.civet

{
  floor
  round
} := Math

type { MidiEvent } from @danielx/lib

export type MoveEvents = MidiEvent[] & {
  // note event -> [staff position, semitones off the key's scale note, key]
  // where it started, so every move is measured from there
  originalStaffPositions: Map<MidiEvent, [number, number, number]>
  staffOffset: number
  semitoneOffset: number
}

export class Player extends Base with PlayerAudio, Spritesheet, Persistence, MidiDeviceInput, PracticeMode, Renderer, ToolsMixin
  persistenceElement: HTMLElement
  aboutTemplate: HTMLElement
  adSlot: ReturnType<typeof AdSlot>
  buyNowTemplate: HTMLElement
  element: HTMLElement
  patternPreviewElement: HTMLElement
  signaturePickerElement: HTMLElement
  noteControlElement: HTMLElement
  midiInputElement: HTMLElement
  meterPickerElement: HTMLElement

  notify(message: any)
    // TODO
    console.log message

  // Notify the user of an error or warning
  notifyWarning(message: any)
    // TODO
    console.warn message

  // Returns the hide, which takes down this loading view only — never a modal
  // that has replaced it since
  showLoading(message="Loading...")
    progressView := Progress
      value: 0
      message: message

    Modal.show progressView.element,
      cancellable: false

    ->
      Modal.hide() if progressView.element.isConnected

  notePosition = Observable ""

  addNote = (c: number, n: number, v: number, beat: number, duration: number) =>
    @unsaved true
    n = clamp n, 0, 127
    @song.addNote c, n, v, beat, duration

    @pushState @getState()

  // pos is {beat, note} (midi beat, midi note)
  removeNote: (typeof @song)["removeNote"] = (pos, within) =>
    @unsaved true
    removed := @song.removeNote pos, within
    @pushState @getState()

    return removed

  setProgram = (c: number, p: number) =>
    @unsaved true
    @song.setProgram(c, p)
    @pushState @getState()

    // Dispatch programChange midi event to player-audio context
    @dispatchImmediateMidiData new Uint8Array [0xc0 + (c & 0xf), p & 0x7f]

  setPan = (c: number, v: number) =>
    // TODO: figure out a good way to work these into undo/redo without being
    // obnoxious. It shouldn't crush your undo stack to move the slider
    // all the mixing of volume and pan would ideally flatten into a single
    // state change that is applied when the settings window is closed.
    // @unsaved true
    @song.setPan(c, v)
    // @pushState @getState()

    // Dispatch pan cc midi event to player-audio context
    @dispatchImmediateMidiData new Uint8Array [0xb0 + (c & 0xf), 0x0a, v & 0x7f]

  setVolume = (c: number, v: number) =>
    // see above note
    // @unsaved true
    @song.setVolume(c, v)
    // @pushState @getState()

    // Dispatch volume cc midi event to player-audio context
    @dispatchImmediateMidiData new Uint8Array [0xb0 + (c & 0xf), 0x07, v & 0x7f]

  noteOnEventsNear = (pos: BeatNote, within: number) =>
    @song.noteOnEventsNear(pos, within)

  // events is Array<MidiEvent> with additional information about the start
  // note and staff position for noteOn/Off events.
  // Staff moves step along each note's own key; semitone moves are chromatic.
  moveEvents = (events: MoveEvents, beatDelta: number) =>
    {originalStaffPositions, staffOffset, semitoneOffset} := events

    events.forEach (e) =>
      e.tick += floor beatDelta * @song.ticksPerBeat

      if e.note?
        [staffNote, offset, key] := originalStaffPositions.get(e)!
        note := staffNoteToMidiPitch(staffNote + staffOffset, offset, key) + semitoneOffset
        e.note = clamp note, 0, 127

  // Add the events in a pattern at a particular note and beat position
  // play a preview sound
  addPattern = (beatNote: BeatNote, pattern: Pattern) =>
    @unsaved true

    {beat, note} := beatNote
    stpb := @song.ticksPerBeat
    tick := floor beat * stpb

    events := pattern.events.map (e) ->
      e = e.copy()
      if e.note?
        e.note = note + e.note - 60

      return e

    ptpb := pattern.ticksPerBeat

    // Play the preview audio before offsetting the tick
    @playPatternPreview
      events: events
      ticksPerBeat: ptpb

    events.forEach (e) ->
      // convert pattern ticksPerBeat to song ticksPerBeat
      e.tick = floor tick + e.tick / ptpb * stpb

    @song.addEvents events

    @pushState @getState()

  deleteRange = (range: SelectionRange) =>
    @unsaved true
    @song.deleteRange range

    @pushState @getState()

  // Copy a selection range to a pattern that can be reused
  copyRange = (range: SelectionRange) =>
    pattern := @song.patternFromRange range

    if pattern
      pattern.preview = @createPatternPreview(pattern)
      pattern.ticksPerBeat = @song.ticksPerBeat

      // Create and select pattern
      @activePatternIndex @patterns.push(pattern) - 1
      // Note: this must be second so we don't play the preview sound for the
      // previously selected pattern index
      @activeToolIndex 3 // Pattern palette

  toggleLoop = =>
    @loop !@loop()

  loopButtonClass = =>
    "active" if @loop()

  playButtonClass = =>
    "active" if @playing()

  // Gets / sets the tempo of the event at the current beat: the most recent
  // setTempo event at or before the playhead, which is the one the input is
  // displaying. Placing a new change event is the setTempo tool's job, not
  // this one's.
  tempo = (...args: [] | [string]): string =>
    @song.tempoMapChanged() // Update when tempo map changes

    e := @song.tempoEventAtBeat @currentBeat()

    if args.length
      bpm := parseFloat(args[0])
      if bpm > 0
        tempo := round 60000000 / bpm
        if @song.setTempoAtBeat @currentBeat(), clamp tempo, 0, (1<<24)-1
          @unsaved true
    else
      inv := 60000000 / e.microsecondsPerBeat
      inv.toFixed(2).replace(/\.00$/, "")

  addTempoChange = (beat: number) =>
    if @song.addSetTempoEvent beat
      @pushState @getState()

  removeTempoChange = (beat: number, within=0.25) =>
    predicate := ({type}) ->
      type is "setTempo"

    if @song.removeEvent beat, within, predicate
      @pushState @getState()

  afxPresetName = (...args: [] | [string]): string =>
    if args.length
      @song.afxPresetName(args[0])
    else
      @song.afxPresetName() or "slapback"

  vfxPresetName = (...args: [] | [string]): string =>
    if args.length
      @song.vfxPresetName(args[0])
    else
      @song.vfxPresetName() or "Purple"

  // The preset names, for a caller that has to pick one without a UI (app/render-cli.js).
  // Worth having as an accessor rather than letting the caller guess: presetByName falls
  // back to the default silently, so a misspelled name renders the wrong thing and looks
  // like it worked.
  vfxPresetNames = (): string[] =>
    Shaders.shaderToyShaders.map ({name}: {name: string}) -> name

  vfxSource = (...args: [] | [string]): string? =>
    if args.length
      @song.vfxSource args[0]
    else
      if @song.vfxPresetName() is CUSTOM_PRESET_NAME
        @song.vfxSource() or Shaders.example
      else
        undefined

  timeSignature = (...args: [] | [string]): string =>
    @song.timeSignatureMapChanged() // Update when time signature map changes

    e := @song.timeSignatureEventAtBeat @currentBeat()

    if args.length
      [numerator, denominator] := args[0].split("/").map (s) -> parseInt(s, 10)

      if @song.setTimeSignatureAtBeat @currentBeat(), numerator, denominator
        @unsaved true

    else
      {numerator, denominator} := e

      `${numerator}/${denominator}`

  addTimeSignature = (beat: number) =>
    if @song.addTimeSignature beat
      @pushState @getState()

  removeTimeSignature = (beat: number, within=0.25) =>
    predicate := ({type}) ->
      type is "timeSignature"

    if @song.removeEvent beat, within, predicate
      @pushState @getState()

  keySignature = (...args) =>
    @song.keySignatureMapChanged() // Update when keySignatureMap changes

    e := @song.keySignatureEventAtBeat @currentBeat()

    if args.length
      [k, m] := args
      if @song.setKeySignatureAtBeat @currentBeat(), k, m
        @unsaved true
    else
      e

  addKeySignature = (beat: number) =>
    if @song.addKeySignature beat
      @pushState @getState()

  removeKeySignature = (beat: number, within=0.25) =>
    predicate := ({type}) ->
      type is "keySignature"

    if @song.removeEvent beat, within, predicate
      @pushState @getState()

  // TODO: Clear song vs clear section
  clearDisabled = =>
    // TODO: Update for Midi

  clear = =>
    if await Modal.confirm "Clear entire song?"
      @song.clear()
      @unsaved true
      @pushState @getState()

  about = =>
    Modal.show @aboutTemplate,
      cancellable: true

  showHelp = =>
    Modal.show HelpTemplate(@),
      cancellable: true

  showNewsletterSignUpForm = =>
    window.open "https://danielx.us14.list-manage.com/subscribe?u=1a0a190706af1297abfe1e208&id=ebee0be266", "_blank"

  showPersistenceModal = =>
    Modal.show @persistenceElement

  hiddenIfPurchased = =>
    "hidden" if @purchased()

  hiddenUnlessPurchased = =>
    "hidden" unless @purchased()

  // Rendering hands frames to ffmpeg in the desktop app's main process
  hiddenUnlessCanRender = =>
    "hidden" unless @purchased() and global.ELECTRON_VERSION

  purchase = =>
    // preload
    loadStripe()
    // Asked for while it is already up (the hotkey) is the same showing
    @adSlot.rotate() unless @buyNowTemplate.isConnected
    Modal.show @buyNowTemplate,
      cancellable: true

  stripePurchase = =>
    purchase()

  feedback = =>
    window.open "https://docs.google.com/forms/d/e/1FAIpQLSeRz9rCsLJLacvpJNAtAPhj0AN0LM155INP01Y8Tt4k2pIlmA/viewform", "_blank"

  discord = =>
    window.open "https://discord.gg/wcpWDNk", "_blank"

  versionInfo = =>
    `DanielX.net Paint Composer (version ${require("../package.json").version})`

  exportAudio = =>
    name := Observable "song"
    selectedType := Observable "mp3"
    formatTypes := ["mp3", "wav"]

    Modal.show ExportTemplate
      name: name
      title: generateExportTitle()
      selectedType: selectedType
      formatOptionElements: =>
        formatTypes.map (type) =>
          OptionTemplate
            text: type
            value: type

      cancel: (e: Event) =>
        e.preventDefault()
        Modal.hide()

      submit: (e: Event) =>
        e.preventDefault()

        @exportSong @song,
          name: name()
          type: selectedType()

  usesPaidFeatures = :boolean =>
    sha := @song.synthSha()

    // TODO: this isn't really the best check
    sha && !(sha is DEFAULT_SYNTH_SHA or sha is LEGACY_SYNTH_SHA)

  paymentRequired = =>
    needsPaid := @usesPaidFeatures() and !@purchased()

    if needsPaid
      // show buy now modal
      @purchase()

    return needsPaid

  patterns = Observable []
  purchased = Observable false
  song = new MidiSong()
  theme = Observable ""

  // Delegating to song
  loop = @song.loop
  length = @song.length
  upcomingNotes = @song.upcomingNotes

  // Move to system?
  fullscreen()
    if document.fullscreenElement
      document.exitFullscreen()
    else
      document.body.requestFullscreen()

  loadFromURL = (url: string) =>
    progressView := Progress
      value: 0
      message: "Loading..."

    Modal.show progressView.element,
      cancellable: false

    ajax.ajax
      url: url
      responseType: "json"
    .progress ({lengthComputable, loaded, total}) ->
      if lengthComputable
        progressView.value loaded / total
    .then @fromJSON
    .then ->
      Modal.hide()
    .catch (e) ->
      if e.statusText
        Modal.alert `An error has occurred: ${e.status} - ${e.statusText}`
      else
        Modal.alert `An error has occurred: ${e.message}`

  @(I={})
    super I

    // debug events
    // @on "*", console.log

    @theme.observe (theme) ->
      el := document.documentElement
      el.classList.forEach (c) ->
        if c.startsWith "theme-"
          el.classList.remove c

      if theme
        el.classList.add "theme-" + theme

      try
        localStorage.setItem(THEME_KEY, theme)

    try
      @theme localStorage.get(THEME_KEY)

    RenderModal(@)
    ThemeEditor(@)

    // This common convention passes self as a model to a sub-view and attaches the
    // view's element to be rendered in our templates.
    @noteControlElement = NoteControl(@).element

    // Analyser View
    // stereoAnalyserView = require("./views/stereo-analyser")(context)
    // @stereoAnalyserElement = stereoAnalyserView.element

    @midiInputElement = MidiInput(@).element

    // The mode modal must exist before Actions below: the actions bar's
    // Presenter copies player methods at construction, showModeModal included
    ModeModal(@)
    MyPractice(@)
    PracticeSetup(@)
    SongCollection(@)
    GameResults(@)

    // Meter Picker
    @meterPickerElement = MeterPicker(@).element

    // Meter Picker
    @signaturePickerElement = SignaturePicker(@).element

    VFXEditor(@)

    // FX Picker
    @fxPickerElement = FXPicker({
      vfxPresetName: @vfxPresetName
      afxPresetName: @afxPresetName
      showVFXEditor: @showVFXEditor
    }).element

    // Demo song picker
    DemoPicker(@)
    // Account song picker
    AccountSongPicker(@)

    // Channel / Sprite Config
    Settings(@)

    @element = AppTemplate
      actionsElement: Actions(@).element
      toolsElement: Tools(@).element
      patternsElement: Patterns(@).element
      staffElement: @viewportElement
      notePosition: @notePosition

    @element.querySelector("viewport")!.appendChild @selectionElement
    // the game HUD is a Pixi layer now (game-hud-layer.civet, in Renderer)
    @element.querySelector("viewport")!.appendChild ModeHud(@).element

    @aboutTemplate := AboutTemplate(@)
    @adSlot := AdSlot(@)
    @buyNowTemplate := BuyNowTemplate
      adSlot: @adSlot.element
      stripePurchase: =>
        @stripePurchase()
      submit: (e) ->
        e.preventDefault()
        purchase()

    @persistenceElement := PersistenceTemplate(@)
    @patternPreviewElement = @element.querySelector("pattern-preview")!

    // TODO: These can be removed once Bindable is a mixin that gets added first
    @initSpritesheet()
    @initMidiInput()
    @initPractice()
    @initPlayerAudio()
    @initRenderer()
    @initTools()

generateExportTitle := ->
  adjective := rand [
    "cool"
    "rad"
    "kickin'"
    "bumpin'"
    "sweet"
    "tasty"
  ]

  noun := rand [
    "banger"
    "track"
    "song"
    "tune"
    "jam"
  ]

  `Export your ${adjective} ${noun}`

export default Player
